import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

function formatDollar(cents) {
  return "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

const TIER_META = {
  general: {
    tagline: "1 seat · dinner · open networking",
  },
  vip: {
    tagline: "Priority seating · welcome cocktail · VIP reception · swag bag",
  },
  sponsor_table: {
    tagline: "Full table (8 seats) · program mention · reserved seating",
  },
  founding_sponsor: {
    tagline: "Full VIP table · logo on stage · full-page program ad · stage recognition",
  },
};

export default function BuyTicketsModal({ open, onClose, event }) {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/payments/tickets").then((r) => setTickets(r.data)).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const total = selected ? selected.amount * quantity : 0;

  const checkout = async () => {
    if (!selected) { toast.error("Choose a ticket first"); return; }
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected.lookup_key,
        quantity,
        email,
        origin_url: window.location.origin,
        event_id: event?.id,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Payment initiation failed");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="buy-tickets-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-4xl bg-bdu-surface border border-bdu-gold/30 my-8" role="dialog" aria-modal="true">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-bdu-cream/60 hover:text-bdu-gold transition-colors"
          data-testid="buy-tickets-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-8 sm:p-10">
          <div className="label-eyebrow">Purchase Tickets</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-bdu-cream tracking-tight">
            {event?.title || "Black Royal Unity Ball"}
          </h2>
          {event?.subtitle && (
            <div className="mt-2 font-serif italic text-bdu-gold/90 text-lg">{event.subtitle}</div>
          )}

          <div className="mt-8 space-y-3" data-testid="buy-tickets-list">
            {tickets.map((t) => {
              const isSelected = selected?.lookup_key === t.lookup_key;
              const meta = TIER_META[t.tier] || {};
              return (
                <button
                  key={t.lookup_key}
                  type="button"
                  onClick={() => setSelected(t)}
                  className={`w-full text-left px-5 py-4 border transition-all ${
                    isSelected
                      ? "border-bdu-gold bg-bdu-gold/[0.06]"
                      : "border-bdu-gold/15 hover:border-bdu-gold/40"
                  }`}
                  data-testid={`ticket-option-${t.tier}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="font-serif text-xl text-bdu-cream">{t.label}</div>
                      <div className="mt-1 text-xs text-bdu-cream/50 tracking-wide">{meta.tagline}</div>
                    </div>
                    <div className="font-serif text-2xl text-bdu-gold tabular-nums shrink-0">{formatDollar(t.amount)}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Quantity + email */}
          <div className="mt-8 grid sm:grid-cols-[1fr_2fr] gap-4">
            <div>
              <div className="label-eyebrow mb-3">Quantity</div>
              <div className="flex items-center border border-bdu-gold/25">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 text-bdu-cream/70 hover:text-bdu-gold transition-colors"
                  data-testid="ticket-qty-decrease"
                >−</button>
                <div className="flex-1 text-center font-serif text-xl text-bdu-cream" data-testid="ticket-qty">{quantity}</div>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(50, quantity + 1))}
                  className="px-4 py-3 text-bdu-cream/70 hover:text-bdu-gold transition-colors"
                  data-testid="ticket-qty-increase"
                >+</button>
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Email for Confirmation</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold"
                data-testid="ticket-email-input"
              />
            </div>
          </div>

          {/* Disclosure */}
          <div className="mt-6 border border-bdu-red/25 bg-bdu-red/[0.04] px-4 py-3 text-xs text-bdu-cream/80 leading-relaxed" data-testid="ticket-disclosure">
            <strong className="text-bdu-red uppercase tracking-widest text-[0.6rem]">Notice · </strong>
            Ticket purchases are <strong>not tax-deductible contributions</strong>. TDSP Fund Inc. is not currently a 501(c)(3). Payment is processed by Stripe and deposited into the TDSP Fund's Citizen Trust Bank account.
          </div>

          {/* Total + checkout */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-bdu-gold/15">
            <div>
              <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Total</div>
              <div className="mt-1 font-serif text-4xl text-bdu-gold tabular-nums" data-testid="ticket-total">
                {formatDollar(total)}
              </div>
              <div className="text-xs text-bdu-cream/40 mt-1">Sales tax calculated at checkout by state.</div>
            </div>
            <button
              onClick={checkout}
              disabled={submitting || !selected}
              className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ticket-checkout-btn"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Redirecting...</> : "Continue to Secure Checkout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
